import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import type { IntentValuesType } from 'Deer/intent/types/IntentCommonTypes';

import { past3Months } from 'constants/ProjectConstants';

export type EngagementDateRange = { label?: string; startDate: Date; endDate: Date };

export type EngagementIntentType = {
  id: string;
  label: string;
  onClick: () => void;
  visible: () => boolean;
  value: {
    myData?: IntentValuesType;
  };
  queryParamValue?: string;
  category: 'Manage' | 'Analyze' | 'Configure';
  showTimelineDropdown?: boolean;
  showSurveyDropdown?: boolean;
  showSplitByDropdown?: boolean;
  showViewByDropdown?: boolean;
  showViewByEmployeeFunctionDropdown?: boolean;
  isDefault?: boolean;
  hasMetaFilters?: boolean;
  showCompanyDropdown?: boolean;
  showLocationDropdown?: boolean;
};

const initialState: {
  selectedIntent?: EngagementIntentType;
  splitByOptions?: {
    uuid?: string;
    keyName?: string;
    displayName?: string;
    enabled?: boolean;
  }[];
  viewByOptions?: {
    uuid?: string;
    keyName?: string;
    displayName?: string;
    enabled?: boolean;
  }[];
  selectedDateRange?: EngagementDateRange;
  selectedSplitBy?: {
    value: string;
    label: string;
    uuid: string;
  };
  selectedViewBy?: {
    value: string;
    label: string;
    uuid: string;
  };
  surveys?: {
    uuid?: string;
    keyName?: string;
    displayName?: string;
    enabled?: boolean;
    performanceEvaluationGrid?: unknown | null;
    reviewType?: string;
    feedbackForms?: unknown | null;
    visibilityType?: 'ANONYMOUS' | 'SEMI_ANONYMOUS' | 'NON_ANONYMOUS';
  }[];
  rowGroupColsOptions?: {
    id: string;
    displayName: string;
    field: string;
  }[];
  selectedRowGroupCols?: {
    id: string;
    displayName: string;
    field: string;
  }[];
  selectedCompanies?: number[];
  selectedLocations?: string[];
} = {
  selectedIntent: null,

  splitByOptions: [
    {
      uuid: 'NONE',
      keyName: 'NONE',
      displayName: 'None',
      enabled: true,
    },
    {
      uuid: 'FUNCTION_NAME',
      keyName: 'FUNCTION_NAME',
      displayName: 'Function',
      enabled: true,
    },
    {
      uuid: 'ROLE_NAME',
      keyName: 'ROLE_NAME',
      displayName: 'Role',
      enabled: true,
    },
    {
      uuid: 'LOCATION',
      keyName: 'LOCATION',
      displayName: 'Location',
      enabled: true,
    },
    {
      uuid: 'COMPANY_NAME',
      keyName: 'COMPANY_NAME',
      displayName: 'Company name',
      enabled: true,
    },
  ],

  viewByOptions: [
    {
      uuid: 'QUESTION_DESCRIPTION',
      keyName: 'QUESTION_DESCRIPTION',
      displayName: 'Question Description',
      enabled: true,
    },
  ],
  selectedDateRange: past3Months,
  selectedSplitBy: {
    value: 'none',
    label: 'None',
    uuid: null,
  },
  selectedViewBy: {
    value: 'QUESTION_DESCRIPTION',
    label: 'Question Description',
    uuid: null,
  },
  surveys: null,
  rowGroupColsOptions: [
    {
      id: 'EMPLOYEE',
      displayName: 'Employee',
      field: 'user_emp_id',
    },
    {
      id: 'DIVISION',
      displayName: 'Division',
      field: 'division',
    },
  ],
  selectedRowGroupCols: [
    {
      id: 'EMPLOYEE',
      displayName: 'Employee',
      field: 'user_emp_id',
    },
  ],
  selectedCompanies: [],
  selectedLocations: [],
};

export const engagementIntentSlice = createSlice({
  name: 'engagementIntent',
  initialState,
  reducers: {
    setSelectedEngagementIntent: (state, action: PayloadAction<EngagementIntentType>) => {
      const newState = { ...state };
      newState.selectedIntent = action.payload;
      return newState;
    },
    setSelectedSplitByEngagementIntent: (
      state,
      action: PayloadAction<{ value: string; label: string; uuid: string }>
    ) => {
      state.selectedSplitBy = action.payload;
    },
    setSelectedViewByEngagementIntent: (
      state,
      action: PayloadAction<{ value: string; label: string; uuid: string }>
    ) => {
      state.selectedViewBy = action.payload;
    },
    setSelectedDateRangeEngagementIntent: (state, action: PayloadAction<EngagementDateRange>) => {
      state.selectedDateRange = action.payload;
    },
    setEngagementSurveysEngagementIntent: (
      state,
      action: PayloadAction<{ surveys: (typeof initialState)['surveys'] }>
    ) => {
      state.surveys = action.payload.surveys;
    },
    setEngagementSelectedCompanies: (state, action: PayloadAction<number[]>) => {
      state.selectedCompanies = action.payload;
    },
    setEngagementSelectedLocations: (state, action: PayloadAction<string[]>) => {
      state.selectedLocations = action.payload;
    },
  },
});

export const {
  setSelectedEngagementIntent,
  setSelectedSplitByEngagementIntent,
  setSelectedViewByEngagementIntent,
  setSelectedDateRangeEngagementIntent,
  setEngagementSurveysEngagementIntent,
  setEngagementSelectedCompanies,
  setEngagementSelectedLocations,
} = engagementIntentSlice.actions;

export default engagementIntentSlice.reducer;

export const useSelectEngagementSelectedIntent = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.selectedIntent);

export const useSelectEngagementSplitByOptions = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.splitByOptions);

export const useSelectEngagementViewByOptions = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.viewByOptions);

export const useSelectEngagementSelectedSplitBy = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.selectedSplitBy);

export const useSelectEngagementSelectedViewBy = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.selectedViewBy);

export const useSelectEngagementSelectedDateRange = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.selectedDateRange);

export const useSelectEngagementSurveys = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.surveys);

export const useSelectEngagementRowGroupColsOptions = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.rowGroupColsOptions);

export const useSelectEngagementSelectedCompanies = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.selectedCompanies);

export const useSelectEngagementSelectedLocations = () =>
  useSelector((state: { engagementIntent: typeof initialState }) => state.engagementIntent.selectedLocations);
